"use client";

import { cn } from "@/lib/utils";
import { useTranslation } from "@/app/i18n/client";
import {
  PriceOptions,
  SubscriptionProductIDs,
  TRIAL_PERIODS,
} from "@/constants/stripeConstants";
import { DefaultLocales, LocaleName } from "@/app/i18n/settings";
import Link from "next/link";
import { StripeSubscriptionPriceWithTiers } from "@/types/stripe";
import { getLocalizedRoute } from "@/utils/localized-route";

type PaymentCardProps<T extends SubscriptionProductIDs> = {
  lang: LocaleName;
  priceType: PriceOptions<T>;
  StripePrice: StripeSubscriptionPriceWithTiers<NoInfer<T>, PriceOptions<T>>;
  productType: T;
  className?: string;
  isRecommended?: boolean;
  disableTestPhase?: true;
  onClick?: (price: PriceOptions<T>) => void;
};

export default function PaymentCard<T extends SubscriptionProductIDs>({
  lang,
  priceType,
  StripePrice,
  productType,
  className,
  isRecommended,
  disableTestPhase,
  onClick,
}: PaymentCardProps<T>) {
  const { t, i18n } = useTranslation("paymentCard");

  const isWithTestPhase =
    !disableTestPhase &&
    TRIAL_PERIODS[
      `${productType}.${String(priceType)}` as keyof typeof TRIAL_PERIODS
    ];

  const euroFormatter = new Intl.NumberFormat(
    DefaultLocales[i18n.language as LocaleName].baseName,
    {
      style: "currency",
      currency: StripePrice.currency ?? "EUR",
      minimumFractionDigits: 0,
    },
  );

  const add = className != undefined ? className : "";

  if (!isRecommended) {
    className =
      "border-solid border-2 border-matchbus lg:w-fit mt-6 mr-6 bg-white " +
      add;
  } else className = "bg-babyBlue " + add;

  return (
    <>
      <div
        className={cn(
          isRecommended &&
            "mr-6 mt-6 box-border flex w-full flex-col rounded-2xl border-2 border-solid border-matchbus bg-matchbus lg:mt-0 lg:w-fit",
        )}
      >
        {isRecommended && (
          <div className="h-6">
            <p className="m-0 text-center italic text-white">
              {t("RECOMMENDED")}
            </p>
          </div>
        )}
        <div
          className={cn(
            "box-border w-full rounded-2xl p-4 text-center text-matchbus",
            className,
          )}
        >
          <p className="text-2xl font-bold not-italic text-secondary">
            {t("TITLE", {
              interval: t(
                `TIMEFRAMES.${priceType.toString()}` as `TIMEFRAMES.${PriceOptions<"AdPartner">}`,
              ),
            })}
          </p>
          <p className="m-2 text-7xl">
            {euroFormatter.format(StripePrice.basePrice / 100)}
          </p>
          <p className="m-2">{t("TAX")}</p>
          <p className="mx-auto max-w-[80%] text-base font-bold not-italic text-secondary">
            {t("CAPTION")}
          </p>
          <p className="m-0 text-5xl">
            {StripePrice.upperPrices.length > 0
              ? euroFormatter.format(StripePrice.upperPrices[0][1].price / 100)
              : "???"}
          </p>
          <p className="m-2">{t("TAX")}</p>
          <p className="mt-10">
            {onClick ? (
              <button
                className="block w-full rounded-xl bg-action px-4 py-2 text-xl font-bold italic text-white"
                onClick={() => onClick(priceType)}
              >
                {isWithTestPhase
                  ? t("BUTTON.STARTTESTPHASE")
                  : t("BUTTON.PAYNOW")}
              </button>
            ) : (
              productType === "AdPartner" && (
                <Link
                  href={getLocalizedRoute(
                    lang,
                    `register/advertiser/data?priceType=${priceType.toString()}`,
                  )}
                  className="block rounded-xl bg-action px-4 py-2 text-xl font-bold italic text-white"
                >
                  {isWithTestPhase
                    ? t("BUTTON.STARTTESTPHASE")
                    : t("BUTTON.PAYNOW")}
                </Link>
              )
            )}
          </p>
        </div>
      </div>
    </>
  );
}
